<template>
  <div
    v-if="isLoading"
    class="w-full mt-2 flex justify-center"
  >
    <BaseSpinner />
  </div>
  <component
    :is="coursePageLayoutComponent"
    v-else
    v-bind="$props"
    :tabs="tabs"
    :page="page"
    :department="department"
    :programs="programs"
    :courses="courses"
    :initial-tab="activeTab"
  >
    <template #sidebar>
      <slot name="sidebar">
        <div
          v-if="enableSidebar"
          class="md:w-1/4 md:block ml-12 mb-8 hidden"
        >
          <SidebarContent :content="sidebarContent" />
        </div>
      </slot>
    </template>
  </component>
</template>

<script>
import { mapState } from 'vuex'
import _sortBy from 'lodash/sortBy'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  DEPARTMENT_CATALOG_DESCRIPTIONS,
  SIDEBAR,
  DEPARTMENTS_PAGE_TAB_LAYOUT,
  CATALOG_DEPARTMENTS_PAGE_LAYOUT_SETTINGS,
} from '@/constants/featureFlags'
import SidebarContent from '@/components/contemporaryTheme/components/SidebarContent'
import DepartmentPageBaseLayout from '~contemporaryTheme/pages/departments/_id.baseLayout'
import DepartmentPageTabLayout from '~contemporaryTheme/pages/departments/_id.tabLayout'
import ProgramsService from '@/services/programs'
import CoursesService from '@/services/courses'
import { capitalize, safeEncodeURIComponent } from '@/utils/generalUtils'
import { departmentRelatedProgramsFields, departmentRelatedCoursesFields } from '@/constants/departments'
import useMeta from '@/composables/useMeta'
import useDepartmentColumns from '@/composables/useDepartmentColumns'

const DEFAULT_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'courses', label: 'Courses' },
  { key: 'programs', label: 'Programs' },
]

export default {
  components: {
    SidebarContent,
  },
  props: {
    allowedTabs: {
      type: Array,
      /* [{ to: 'overview', label: 'Overview' }, ...] */
      default: null,
    },
    sidebarContent: {
      type: Array,
      default: () => [
        /*
          title: String,
          description: String,
          image: Image,
          cta: Array
        */
      ],
    },
  },
  setup() {
    const {
      enableCatalogDescriptions,
      enableSidebar,
      departmentPageTabLayout,
      applyDepartmentPageLayoutSettings,
    } = useFeatureFlags({
      enableCatalogDescriptions: DEPARTMENT_CATALOG_DESCRIPTIONS,
      enableSidebar: SIDEBAR,
      departmentPageTabLayout: DEPARTMENTS_PAGE_TAB_LAYOUT,
      applyDepartmentPageLayoutSettings: CATALOG_DEPARTMENTS_PAGE_LAYOUT_SETTINGS,
    })
    const { getColumns } = useDepartmentColumns()
    const { catalogWideMetaTitle } = useMeta()
    return {
      enableCatalogDescriptions,
      enableSidebar,
      departmentPageTabLayout,
      applyDepartmentPageLayoutSettings,
      catalogWideMetaTitle,
      getColumns,
    }
  },
  data() {
    return {
      availableTabs: [],
      department: {},
      page: {},
      programs: [],
      courses: [],
      isLoading: true,
    }
  },
  head() {
    return {
      title: `${this.department.displayName} Department | ${
        this.catalogWideMetaTitle
      }`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.department.displayName} Department | ${
            this.catalogWideMetaTitle
          }`,
        },
      ],
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    activeTab() {
      return this.$route.path.split('/').pop()
    },
    coursePageLayoutComponent() {
      if (this.departmentPageTabLayout) return DepartmentPageTabLayout
      return DepartmentPageBaseLayout
    },
    tabs() {
      return this.availableTabs
    },
  },
  async created() {
    try {
      const settings = await this.fetchSettings()
      this.$store.commit('departments/SET_PAGE_LAYOUT_SETTINGS', settings?.publicCatalogDepartmentsPageLayoutSettings)

      const url = `/ca/${this.school}/catalogPages/${
        this.$store.state.settings.activeCatalog
      }/department-${safeEncodeURIComponent(this.$route.params.id)}`

      const departmentPageDataRequests = [
        this.$axios.$get(
          `/${this.school}/departments/${safeEncodeURIComponent(
            this.$route.params.id
          )}`
        ),
        this.$axios.$get(url).catch((e) => {
          console.warn('Missing department page')
        }),
        this.fetchPrograms(),
        this.fetchCourses(),
      ]

      if (
        this.applyDepartmentPageLayoutSettings &&
        settings?.publicCatalogDepartmentsPageLayoutSettings
      ) {
        this.availableTabs = settings.publicCatalogDepartmentsPageLayoutSettings
          .filter((tab) => tab.enabled)
          .map((tab) => ({ ...tab, key: tab.id, label: capitalize(tab.id) }))
      } else if (this.allowedTabs) {
        this.availableTabs = this.allowedTabs
      } else {
        this.availableTabs = DEFAULT_TABS
      }

      const [department, page, programs, courses] = await Promise.all(
        departmentPageDataRequests
      )
      this.department = department
      this.page = page
      this.programs = programs
      this.courses = courses
      this.availableTabs = this.availableTabs.filter((tab) => {
        if (tab.key === 'courses') {
          return !!this.courses.length
        } else if (tab.key === 'programs') {
          return this.programs.length
        }

        return true
      })
    } catch (e) {
      this.$bugsnag.notify(e)
      this.$router.push('/404')
    } finally {
      if (!this.tabs.find((t) => t.key === this.activeTab)) {
        const path = this.$route.path.replace(this.activeTab, this.tabs[0].key)
        await this.$router.replace(path)
      }
      this.isLoading = false
    }
  },
  methods: {
    async fetchSettings() {
      if (this.applyDepartmentPageLayoutSettings) {
        const data = await this.$axios.$get(
          `/${this.school}/general/publicCatalogDepartmentsPageLayoutSettings`
        )
        return data
      }
      return null
    },
    async fetchCourses() {
      const api = new CoursesService(this.$axios, this.$store)
      const columns = this.getColumns('courses')
      const { data } = await api.getCourses({
        department: safeEncodeURIComponent(this.$route.params.id),
        columns: [ ...departmentRelatedCoursesFields, ...columns ],
      })
      return _sortBy(Object.values(data), ['code', 'longName'])
    },
    async fetchPrograms() {
      const api = new ProgramsService(this.$axios, this.$store)
      const columns = this.getColumns('programs')
      const { data } = await api.getPrograms({
        department: safeEncodeURIComponent(this.$route.params.id),
        columns: [ ...departmentRelatedProgramsFields, ...columns ],
      })
      return _sortBy(Object.values(data), [
        'code',
        'longName',
        'catalogDescription',
      ])
    },
  },
}
</script>
